/**
 * Wind-comfort screening (framework-aligned).
 *
 * Pedestrian wind-comfort screening over a Forma predictive (rapid) wind
 * comfort ground grid, normalized to the canonical `simulation-evidence/v1`
 * contract so it flows through the same report path as engine-produced
 * evidence. Outputs are technical screening evidence only and never assert
 * municipal compliance. The Lawson comfort categories are a reference
 * benchmark, not an Israeli legal limit.
 */

import type {
  EvidenceFinding,
  EvidenceMetric,
  EvidenceStatus,
  SimulationEvidence,
} from './contracts';

import type { FormaPredictiveAnalysisGroundGrid, WindComfortScale } from './index';

/** Highest Lawson comfort category value (0..4, higher = windier / less comfortable). */
const MAX_COMFORT_CATEGORY = 4;

/** Category above which conditions exceed the "sitting" comfort threshold. */
const SITTING_CATEGORY = 2;

/** Category above which conditions exceed the "standing" comfort threshold. */
const STANDING_CATEGORY = 3;

const SCREENING_ONLY_FINDING: EvidenceFinding = {
  code: 'screening-only',
  severity: 'info',
  message: 'Output is technical screening evidence only and does not assert municipal compliance.',
};

const LAWSON_BENCHMARK_FINDING: EvidenceFinding = {
  code: 'lawson-reference-benchmark',
  severity: 'info',
  message:
    'Lawson pedestrian wind-comfort categories are a reference benchmark, not an Israeli legal limit.',
};

/** Pedestrian wind-comfort screening over a Forma predictive wind grid, normalized as evidence. */
export function analyzeWindComfortScreening(
  grid: FormaPredictiveAnalysisGroundGrid,
  options?: { comfortScale?: WindComfortScale },
): SimulationEvidence {
  const cellCount = grid.width * grid.height;
  const isEmpty = cellCount === 0 || grid.grid.length === 0;
  const hasInvalidValue = !isEmpty && gridHasInvalidValue(grid.grid);
  const missingInputs = isEmpty ? ['windGrid'] : [];
  const status = getWindComfortStatus(isEmpty, hasInvalidValue);

  return {
    schemaVersion: 'simulation-evidence/v1',
    studyId: 'wind-comfort',
    source: 'forma-native',
    status,
    confidence: status === 'screening-ready' ? 'high' : isEmpty ? 'low' : 'medium',
    metrics: buildWindComfortMetrics(grid, isEmpty, options?.comfortScale),
    findings: buildWindComfortFindings(isEmpty, hasInvalidValue),
    missingInputs,
  };
}

function getWindComfortStatus(isEmpty: boolean, hasInvalidValue: boolean): EvidenceStatus {
  if (isEmpty) {
    return 'blocked';
  }

  if (hasInvalidValue) {
    return 'needs-review';
  }

  return 'screening-ready';
}

function gridHasInvalidValue(values: Float32Array): boolean {
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];

    if (value === undefined || Number.isNaN(value) || value < 0 || value > MAX_COMFORT_CATEGORY) {
      return true;
    }
  }

  return false;
}

function buildWindComfortMetrics(
  grid: FormaPredictiveAnalysisGroundGrid,
  isEmpty: boolean,
  comfortScale?: WindComfortScale,
): EvidenceMetric[] {
  const metrics: EvidenceMetric[] = [{ label: 'Grid cells', value: grid.width * grid.height }];

  if (comfortScale !== undefined) {
    metrics.push({ label: 'Comfort scale', value: comfortScale });
  }

  if (isEmpty) {
    return metrics;
  }

  const values = Array.from(grid.grid);
  const exceedingSitting = values.filter((value) => value > SITTING_CATEGORY).length;
  const exceedingStanding = values.filter((value) => value > STANDING_CATEGORY).length;
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;

  metrics.push({
    label: 'Fraction exceeding sitting',
    value: roundTwo(exceedingSitting / values.length),
  });
  metrics.push({
    label: 'Fraction exceeding standing',
    value: roundTwo(exceedingStanding / values.length),
  });
  metrics.push({ label: 'Mean category value', value: roundTwo(mean) });

  return metrics;
}

function buildWindComfortFindings(isEmpty: boolean, hasInvalidValue: boolean): EvidenceFinding[] {
  const findings: EvidenceFinding[] = [SCREENING_ONLY_FINDING, LAWSON_BENCHMARK_FINDING];

  if (isEmpty) {
    findings.push({
      code: 'no-wind-grid',
      severity: 'blocker',
      message: 'No wind-comfort grid is available for a pedestrian wind screening.',
    });
  }

  if (hasInvalidValue) {
    findings.push({
      code: 'wind-grid-out-of-range',
      severity: 'warning',
      message: 'Wind-comfort grid contains values outside the expected 0..4 Lawson category range.',
    });
  }

  return findings;
}

function roundTwo(value: number): number {
  return Math.round(value * 100) / 100;
}
